/**
 * @file pages/DevLogin.tsx
 * @brief Login page with clean white background design.
 * @author Nestbotics
 * @date 2026-06-19
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import { Lock, Mail, Eye, EyeOff, CheckSquare } from 'lucide-react';
import Cookies from 'js-cookie';

import { useAppDispatch } from '@/store';
import { setCredentials } from '@/store/slices/authSlice';
import { login, mapBackendUserToFrontend } from '@/services/api/authService';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';

/**
 * DevLogin - Login page; uses backend POST /auth/login.
 * Clean white card layout with Nestbotics branding.
 */
const DevLogin: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useSnackbar();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const payload = await login(email, password);
      const user = mapBackendUserToFrontend(payload.user);
      Cookies.set('authToken', payload.accessToken, { expires: 1 });
      if (payload.refreshToken) {
        Cookies.set('refreshToken', payload.refreshToken, { expires: 7 });
      }
      Cookies.set('userRole', payload.user.role, { expires: 30 });
      Cookies.set('email', payload.user.email, { expires: 30 });
      dispatch(setCredentials({ user, token: payload.accessToken }));
      const redirectTo = (location.state as { from?: string } | null)?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#f1f5f9',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {/* Logo + Brand */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img
            src="/nt_logo.png"
            alt="Nestbotics"
            style={{ height: 64, width: 'auto', objectFit: 'contain', marginBottom: 12 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
            <CheckSquare size={20} color="#2563eb" />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#1e293b', letterSpacing: 0.3 }}
            >
              Task Management
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Powered by Nestbotics
          </Typography>
        </Box>

        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            bgcolor: '#ffffff',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
            Sign in to your account to continue
          </Typography>

          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            {/* Email */}
            <TextField
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              type="email"
              autoComplete="email"
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Mail size={18} color="#94a3b8" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
                },
                '& .MuiInputLabel-root': { color: '#64748b' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' },
                '& .MuiInputBase-input': { color: '#0f172a' },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #f8fafc inset',
                  WebkitTextFillColor: '#0f172a',
                  caretColor: '#0f172a',
                },
              }}
            />

            {/* Password */}
            <TextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={18} color="#94a3b8" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: '#94a3b8', '&:hover': { color: '#475569' } }}
                      type="button"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
                },
                '& .MuiInputLabel-root': { color: '#64748b' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' },
                '& .MuiInputBase-input': { color: '#0f172a' },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #f8fafc inset',
                  WebkitTextFillColor: '#0f172a',
                  caretColor: '#0f172a',
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.975rem',
                bgcolor: '#2563eb',
                boxShadow: '0 1px 3px rgba(37,99,235,0.3)',
                '&:hover': { bgcolor: '#1d4ed8', boxShadow: '0 4px 12px rgba(37,99,235,0.35)' },
                '&:disabled': { bgcolor: '#bfdbfe', color: '#ffffff' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Sign in'}
            </Button>
          </Box>

          <Divider sx={{ my: 3, borderColor: '#f1f5f9' }} />

          <Typography sx={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8' }}>
            Use your Nestbotics account credentials to sign in
          </Typography>
        </Paper>

        {/* Footer */}
        <Typography
          sx={{ textAlign: 'center', mt: 3, fontSize: '0.7rem', color: '#94a3b8' }}
        >
          © {new Date().getFullYear()} Nestbotics. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default DevLogin;
